import Text from 'components/_UI/Text';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import Button from 'components/_UI/Button';
import Fieldset from 'components/_UI/Fieldset';
import TextArea from 'components/_UI/TextArea';
import ImageUpload from 'components/_UI/ImageUpload';
import Loader from 'components/_UI/Loader';
import { Column } from 'styled/alignment/Column';
import { HorizontalCenter, FullyCentered } from 'styled/alignment/Center';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import fieldValidations from 'helpers/fieldValidations';
import masks from 'helpers/masks';
import unformat from 'helpers/unformat';
import imageUploadApi from 'api/requests/imageUpload';
import producerProfilesApi from 'api/requests/producerProfiles';
import ToastContainer from 'components/_UI/ToastsContainer';
import useToast from 'hooks/useToast';
import validateInputFile from 'helpers/validate-input-file';
import allowedFilesTypes from 'texts/messages/allowedFilesTypes';
import buttonText from 'texts/buttons/buttonText';
import { User, Phone, Mail, FileText, Camera, Share2 } from 'lucide-react';
import { panelAccent } from 'theme/panelAccent';
import SocialMedia from './parts/SocialMedia.js';

export default function ProducerProfileForm({ onSuccess, isEdit, onClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const [imgProfile, setImgProfile] = useState(false);
  const [errorImageInfo, setErrorImageInfo] = useState(false);
  const [errorTypeImageProfile, setErrorTypeImageProfile] = useState('');
  const [isUpdateImage, setIsUpdateImage] = useState(false);
  const [tryAgain, setTryAgain] = useState(false);
  const [socialMediaCatalog, setSocialMediaCatalog] = useState([]);
  const [socialMediaContent, setSocialMediaContent] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const { toast, toasts } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      phone: '',
      email: '',
      cpf_cnpj: '',
    }
  });

  useEffect(() => {
    if (isEdit) {
      reset({
        name: isEdit?.name || '',
        description: isEdit?.description || '',
        phone: isEdit?.phone || '',
        email: isEdit?.email || '',
        cpf_cnpj: isEdit?.cpf_cnpj || '',
      });
      setImgProfile(isEdit?.photoImage?.id || false);
    }
  }, [isEdit]);

  useEffect(() => {
    getOptions();
  }, []);

  async function getOptions() {
    setLoadingOptions(true);

    try {
      const { data } = await producerProfilesApi.getOptions();
      const formatedSocialMediaData = data?.socialMedia?.map(el => ({
        label: el.name,
        social_media_id: el.id,
        url: ''
      }));
      setSocialMediaCatalog(formatedSocialMediaData || []);
    } catch (error) {
      // silent
    } finally {
      setLoadingOptions(false);
    }
  }

  async function uploadImageProfile(file) {
    setIsUpdateImage(true);
    const validationFile = validateInputFile(file);

    if (!validationFile.isValid) {
      setErrorTypeImageProfile(validationFile.message);
      setIsUpdateImage(false);
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', 'producer_profile');

    try {
      const { data } = await imageUploadApi.uploadWithFolder(formData);
      setImgProfile(data?.id);
    } catch (error) {
      setErrorImageInfo(true);
    } finally {
      setIsUpdateImage(false);
    }
  }

  async function onToggleDeleteImgProfile() {
    setErrorImageInfo(false);
    setErrorTypeImageProfile(false);
    try {
      await imageUploadApi.delete(imgProfile);
      setImgProfile(null);
    } catch (error) {
      // silent
    }
  }

  function toggleFile(files) {
    uploadImageProfile(files[0]);
  }

  const createPayload = (formData) => {
    const payload = {
      name: formData.name,
      description: formData.description,
      cpf_cnpj: unformat.getNumbers(formData.cpf_cnpj),
    };

    if (formData.phone) {
      payload.phone = unformat.getNumbers(formData.phone);
    }

    if (formData.email) {
      payload.email = formData.email;
    }

    if (imgProfile) {
      payload.photo_image_id = imgProfile;
    }

    payload.social_media = createSocialMediaPayload(socialMediaContent);

    return payload;
  };

  function genericUrlPayload(item) {
    let link = item;

    if (!link) {
      return false;
    }

    if (!link.startsWith('http') && !link.startsWith('www.')) {
      link = `http://${link}`;
    }

    if (!link.startsWith('http') && link.startsWith('www.')) {
      const sanitizedUrl = link.split('www.')[1];
      link = `http://${sanitizedUrl}`;
    }

    return link;
  }

  function createSocialMediaPayload(data) {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data.map(item => {
      const adjustedItem = { ...item };

      if (adjustedItem.label === 'WhatsApp' && !adjustedItem.url.startsWith('http') && !adjustedItem.url.startsWith('www.')) {
        const onlyNumbers = adjustedItem.url.replace(/\D/g, '');
        adjustedItem.url = `http://wa.me/55${onlyNumbers}`;
      } else {
        adjustedItem.url = genericUrlPayload(adjustedItem.url);
      }

      delete adjustedItem.label;

      return adjustedItem;
    });
  }

  const createProfile = async (formData) => {
    setIsLoading(true);
    const payload = createPayload(formData);

    try {
      await producerProfilesApi.create(payload);
      onSuccess('criado');
    } catch (error) {
      if (error?.response?.status === 422) {
        const serverErrors = error?.response?.data?.errors;
        if (serverErrors) {
          const firstError = Object.values(serverErrors)[0]?.[0];
          toast.error(firstError || 'Dados inválidos', { position: 'top-center' });
        }
      } else {
        toast.error('Ocorreu um erro inesperado', { position: 'top-center' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (formData) => {
    setIsLoading(true);
    const payload = createPayload(formData);

    try {
      await producerProfilesApi.update(isEdit?.slug, payload);
      onSuccess('atualizado');
    } catch (error) {
      if (error?.response?.status === 422) {
        const serverErrors = error?.response?.data?.errors;
        if (serverErrors) {
          const firstError = Object.values(serverErrors)[0]?.[0];
          toast.error(firstError || 'Dados inválidos', { position: 'top-center' });
        }
      } else {
        toast.error('Ocorreu um erro inesperado', { position: 'top-center' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormContainer onSubmit={isEdit ? handleSubmit(updateProfile) : handleSubmit(createProfile)}>
      <ToastContainer toasts={toasts} />

      <FormHeader>
        <HeaderIcon>
          <User size={24} color={colors.neutralLight} />
        </HeaderIcon>
        <Text name='subtitle' paddingBottom={0} color={colors.nightDark}>
          {isEdit ? 'Editar' : 'Criar'} perfil de produtor
        </Text>
        <Text name='small' color={colors.night} textAlign='center'>
          {isEdit
            ? 'Essas informações serão exibidas nos seus eventos para que os participantes conheçam você.'
            : 'Crie seu perfil público de produtor. Ele será visível para todos os participantes dos seus eventos.'
          }
        </Text>
      </FormHeader>

      <SectionCard>
        <SectionHeader>
          <SectionIcon><User size={16} color={colors.primaryLight} /></SectionIcon>
          <Text name='small' type='bold' color={colors.nightDark} paddingBottom={0}>Dados do produtor</Text>
        </SectionHeader>

        <AvatarSection>
          <AvatarLabel>
            <Camera size={14} color={panelAccent(colors.occasionalPurple)} />
            <Text name='small' type='bold' color={panelAccent(colors.occasionalPurple)} paddingBottom={0}>
              Foto de perfil
            </Text>
          </AvatarLabel>

          <AvatarUploadArea>
            <ImageUpload
              onToggleFile={toggleFile}
              onDeleteFile={() => onToggleDeleteImgProfile()}
              tryAgain={tryAgain}
              isEdit={isEdit}
              cropAspect={1}
              cropShape='round'
              cropOutputWidth={800}
              cropTitle='Ajustar foto de perfil'
              cropDescription='Arraste para reposicionar e use o zoom. A foto aparece em formato redondo.'
              refreshTryAgain={() => {
                setTryAgain(!tryAgain);
                setErrorImageInfo(false);
              }}
            />
          </AvatarUploadArea>

          <Text name='small' color={colors.nightLight} textAlign='center'>
            {allowedFilesTypes.images} - Máx. 2 MB
          </Text>

          {errorImageInfo && (
            <ErrorRow>
              <Text color={colors.danger} name='small'>Erro ao enviar a imagem.</Text>
              <RetryLink onClick={() => { setTryAgain(true); setErrorImageInfo(false); }}>
                {buttonText.tryAgain}
              </RetryLink>
            </ErrorRow>
          )}

          {errorTypeImageProfile && (
            <ErrorRow>
              <Text name='small' color={colors.danger}>{errorTypeImageProfile}</Text>
              <RetryLink onClick={() => { setTryAgain(true); setErrorImageInfo(false); setErrorTypeImageProfile(false); }}>
                {buttonText.tryAgain}
              </RetryLink>
            </ErrorRow>
          )}
        </AvatarSection>

        <Divider />

        <FieldGroup>
          <Fieldset
            label='Nome do produtor'
            layout='squired'
            name='name'
            validations={fieldValidations.generic('nome do produtor')}
            register={register}
          />
          {errors?.name && (
            <FieldError>{errors.name?.message}</FieldError>
          )}
        </FieldGroup>

        <FieldGroup>
          <Text color={colors.nightDark} type='bold' name='small'>Descrição</Text>
          <TextAreaWrapper>
            <TextArea
              name='description'
              squired={true}
              validations={fieldValidations.generic('descrição', 2, 500)}
              register={register}
            />
          </TextAreaWrapper>
          {errors?.description && (
            <FieldError>{errors.description?.message}</FieldError>
          )}
        </FieldGroup>

        <FieldGroup>
          <Fieldset
            label='CPF ou CNPJ'
            layout='squired'
            name='cpf_cnpj'
            validations={fieldValidations.cpfCnpj('CPF/CNPJ')}
            register={register}
          />
          {errors?.cpf_cnpj && (
            <FieldError>{errors.cpf_cnpj?.message}</FieldError>
          )}
        </FieldGroup>
      </SectionCard>

      <SectionCard>
        <SectionHeader>
          <SectionIcon><Phone size={16} color={colors.primaryLight} /></SectionIcon>
          <Text name='small' type='bold' color={colors.nightDark} paddingBottom={0}>Contato</Text>
          <OptionalBadge>opcional</OptionalBadge>
        </SectionHeader>

        <FieldsRow>
          <FieldGroup>
            <Fieldset
              label='Telefone'
              layout='squired'
              name='phone'
              mask={masks.phone}
              register={register}
            />
          </FieldGroup>

          <FieldGroup>
            <Fieldset
              label='E-mail'
              layout='squired'
              name='email'
              register={register}
            />
            {errors?.email && (
              <FieldError>{errors.email?.message}</FieldError>
            )}
          </FieldGroup>
        </FieldsRow>
      </SectionCard>

      <SectionCard>
        <SectionHeader>
          <SectionIcon><Share2 size={16} color={colors.primaryLight} /></SectionIcon>
          <Text name='small' type='bold' color={colors.nightDark} paddingBottom={0}>Redes sociais</Text>
          <OptionalBadge>opcional</OptionalBadge>
        </SectionHeader>

        <Text name='small' color={colors.nightLight}>
          Adicione os links das suas redes para que apareçam no seu perfil público, visível para quem compra ingresso.
        </Text>

        <SocialMedia
          isEdit={isEdit?.socialMedia}
          data={socialMediaCatalog}
          loading={loadingOptions}
          setSocialMediaContent={setSocialMediaContent}
        />
      </SectionCard>

      <SubmitArea>
        {!isLoading ? (
          <Button
            type="submit"
            category='squired'
            variant={isUpdateImage ? 'disabled' : 'featured'}
          >
            {isEdit ? 'Salvar alterações' : 'Criar perfil de produtor'}
          </Button>
        ) : (
          <FullyCentered>
            <Loader />
          </FullyCentered>
        )}
      </SubmitArea>
    </FormContainer>
  );
}

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  gap: 20px;
  padding-bottom: 24px;
`;

const FormHeader = styled(Column)`
  align-items: center;
  text-align: center;
  gap: 6px;
  padding-bottom: 4px;
`;

const HeaderIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${panelAccent(colors.occasionalPurple)}, ${colors.primaryLight});
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const AvatarSection = styled(Column)`
  align-items: center;
  gap: 8px;
  padding: 8px 0;
`;

const AvatarLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AvatarUploadArea = styled.div`
  & > div {
    justify-content: center;
  }

  & > div > div > div {
    border-radius: 50%;
  }

  & > div > span > div > div > p {
    display: none;
  }
`;

const OptionalBadge = styled.span`
  font-size: ${fonts.sizes.mini};
  color: ${colors.nightLight};
  background: ${colors.neutralDark};
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${colors.neutralDark};
  margin: 4px 0;
`;

const TextAreaWrapper = styled.div`
  & textarea {
    background: ${colors.neutralLight};
    border: 1.5px solid #D4D4D4;
    margin-top: 6px;
  }
`;

const SectionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${colors.neutral};
  border-radius: 12px;
  padding: 20px 16px;
  border: 1px solid rgba(0, 0, 0, 0.04);

  @media screen and (min-width: ${breakpoints.md}) {
    padding: 20px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 4px;
`;

const SectionIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(79, 134, 247, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const FieldGroup = styled.div`
  width: 100%;
`;

const FieldsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media screen and (min-width: ${breakpoints.md}) {
    flex-direction: row;
    gap: 12px;

    & > div {
      flex: 1;
    }
  }
`;

const FieldError = styled.p`
  font-size: ${fonts.sizes.small};
  color: ${colors.danger};
  margin-top: 4px;
  padding-left: 2px;
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  & > p {
    padding-bottom: 0;
  }
`;

const RetryLink = styled.span`
  font-size: ${fonts.sizes.small};
  color: ${colors.primaryLight};
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;

  &:hover {
    color: ${colors.primaryDark};
  }
`;

const SubmitArea = styled.div`
  width: 100%;
  padding-top: 4px;
`;
